import { ELEMENT_CATEGORIES, type ElementCategory } from '@scoutaround/script-breakdown-shared';

/**
 * A GLYPH PER BREAKDOWN CATEGORY, drawn on the product's 24-grid like `Icon`.
 *
 * The category's colour is the industry's (`ELEMENT_CATEGORIES`) and is what a first AD reads
 * first, but colour is never the only signal: every category also has its own shape and its name
 * is always printed beside it. The shapes are generic on purpose — a person, a crowd, a box — so
 * none of them claims more about a category than its name does.
 */
const GLYPH: Readonly<Record<ElementCategory, string>> = {
  CAST: 'M12 4a3.5 3.5 0 1 0 0 7 3.5 3.5 0 0 0 0-7zM5 20c0-3.9 3.1-6.5 7-6.5s7 2.6 7 6.5',
  BACKGROUND:
    'M8 6a2.5 2.5 0 1 0 0 5 2.5 2.5 0 0 0 0-5zM16 6a2.5 2.5 0 1 0 0 5 2.5 2.5 0 0 0 0-5zM3 19c0-3 2.2-5 5-5s5 2 5 5M11 19c0-3 2.2-5 5-5s5 2 5 5',
  STUNTS: 'M13 4a2 2 0 1 0 0 4 2 2 0 0 0 0-4zM6 12l4-2 3 2 3-1M10 10l-1 5 4 2 1 4M9 15l-4 4',
  PROPS: 'M4 8.5 12 4l8 4.5v7L12 20l-8-4.5zM4 8.5 12 13l8-4.5M12 13v7',
  SET_DRESSING: 'M5 11h14v6H5zM7 17v3M17 17v3M5 11V8a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2v3',
  COSTUMES: 'M9 4 4 7l2 4 2-1v10h8V10l2 1 2-4-5-3a3 3 0 0 1-6 0z',
  HAIR_MAKEUP: 'M12 3c-4 0-6.5 3-6.5 7 0 3 1.5 5 1.5 8h10c0-3 1.5-5 1.5-8 0-4-2.5-7-6.5-7zM9 10.5h.01M15 10.5h.01M10 14.5c1.2.8 2.8.8 4 0',
  SFX: 'M12 3c1 3 4 4.5 4 8.5a4 4 0 0 1-8 0c0-1.5.6-2.6 1.4-3.5.2 1.4 1 2.2 1.9 2.4C11 8.8 11 6 12 3zM8 19h8',
  VFX: 'M4 5h16v11H4zM8 20h8M12 16v4M9 9.5l2 2-2 2M13 13.5h3',
  PICTURE_VEHICLES: 'M4 16v-4l2-5h12l2 5v4zM4 16v2h3v-2M17 16v2h3v-2M7.5 13h.01M16.5 13h.01M6.5 12h11',
  ANIMALS:
    'M6 9a1.6 1.6 0 1 0 0 3.2A1.6 1.6 0 0 0 6 9zM10 5.5a1.6 1.6 0 1 0 0 3.2 1.6 1.6 0 0 0 0-3.2zM14 5.5a1.6 1.6 0 1 0 0 3.2 1.6 1.6 0 0 0 0-3.2zM18 9a1.6 1.6 0 1 0 0 3.2A1.6 1.6 0 0 0 18 9zM12 12c-2.8 0-5 3-5 5.2 0 1.6 1.3 2.3 2.7 2 1-.2 1.6-.6 2.3-.6s1.3.4 2.3.6c1.4.3 2.7-.4 2.7-2C17 15 14.8 12 12 12z',
  SPECIAL_EQUIPMENT: 'M4 20h16M8 20 12 7l4 13M12 7V4M9 4h6M6 14h12',
  WEAPONS: 'M5 19 14 10M14 10l2-6 4 4-6 2M7 14l3 3M4 20l2-2',
  GRAPHICS_PLAYBACK: 'M7 3h10v18H7zM10 18h4M9.5 8h5M9.5 11h5',
  CHILDREN_MINORS: 'M12 6.5a2.5 2.5 0 1 0 0 5 2.5 2.5 0 0 0 0-5zM7.5 20c0-3 2-5 4.5-5s4.5 2 4.5 5M12 3v1.5',
  SPECIAL_CREW: 'M12 4a3 3 0 1 0 0 6 3 3 0 0 0 0-6zM6 20c0-3.3 2.7-6 6-6s6 2.7 6 6M8.5 6.5h7',
  EQUIPMENT: 'M3 8h12v9H3zM15 11l6-3v9l-6-3M6 8V6h4v2',
  ANIMAL_WRANGLERS: 'M12 4a3 3 0 1 0 0 6 3 3 0 0 0 0-6zM6 20c0-3.3 2.7-6 6-6s6 2.7 6 6',
  SECURITY_POLICE_FIRE: 'M12 3 4.5 6v5.5c0 4.6 3.2 8.3 7.5 9.5 4.3-1.2 7.5-4.9 7.5-9.5V6z',
  SPECIAL_MAKEUP_PROSTHETICS: 'M12 3c-4 0-6.5 3-6.5 7 0 3 1.5 5 1.5 8h10c0-3 1.5-5 1.5-8 0-4-2.5-7-6.5-7z',
  GREEN_BLUE_SCREEN: 'M4 4h16v13H4zM9 21h6M12 17v4',
  SOUND_PLAYBACK: 'M5 10v4h3l4 4V6L8 10zM16 9a4 4 0 0 1 0 6M18.5 6.5a7.5 7.5 0 0 1 0 11',
  MECHANICAL_EFFECTS: 'M12 8.5a3.5 3.5 0 1 0 0 7 3.5 3.5 0 0 0 0-7zM12 3v3M12 18v3M3 12h3M18 12h3M5.6 5.6l2.1 2.1M16.3 16.3l2.1 2.1',
  INTIMACY_NUDITY: 'M12 20s-7-4.4-7-10a4 4 0 0 1 7-2.6A4 4 0 0 1 19 10c0 5.6-7 10-7 10z',
};

/** A category's tile: its glyph on a light wash of its colour. Decorative: the label is printed beside it. */
export function CategoryIcon({ category, size = 28 }: { category: ElementCategory; size?: number }) {
  const colour = ELEMENT_CATEGORIES[category].colour;

  return (
    <span
      className="el-cat-icon"
      aria-hidden="true"
      style={{ width: size, height: size, background: `${colour}1f`, color: colour, boxShadow: `inset 0 0 0 1px ${colour}38` }}
    >
      <svg
        width={Math.round(size * 0.6)}
        height={Math.round(size * 0.6)}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth={1.8}
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d={GLYPH[category]} />
      </svg>
    </span>
  );
}

/** A category's swatch: a small square of its colour, for chips and legends. */
export function CategorySwatch({ category }: { category: ElementCategory }) {
  return <i className="el-swatch" aria-hidden="true" style={{ background: ELEMENT_CATEGORIES[category].colour }} />;
}
